<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { formatDate } from '@taler/domain'
import {
  Card,
  EmptyState,
  ErrorState,
  IconButton,
  MenuItem,
  MenuPopover,
  NavIcon,
  Skeleton,
  StatusTag,
} from '@taler/ui'
import TargetProgressBar from '@/features/targets/components/TargetProgressBar.vue'
import TargetRangeMeter from '@/features/targets/components/TargetRangeMeter.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  formatMetricValueText,
  hasProgressTreatment,
  statusDisplay,
  statusTone,
  targetLine,
} from '../internal/presentation.ts'
import type { Target } from '../internal/types.ts'

const props = defineProps<{
  targets: readonly Target[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean
  deletingId: string | undefined
  accountLabel: (accountId: string) => string | undefined
}>()
const emit = defineEmits<{ retry: []; delete: [targetId: string] }>()

const { locale, t } = useI18n()

const rows = computed(() =>
  props.targets.map((target) => {
    const loc = locale.value as 'en' | 'hu'
    const evaluation = target.evaluation
    const currentValue = evaluation.currentValue
    const display = statusDisplay(target)
    // The progress bar needs a known current value to draw; without one, fall back to the
    // textual bound line rather than showing neither.
    const showsProgressBar = hasProgressTreatment(target) && currentValue?.kind === 'money'
    return {
      target,
      tone: statusTone(display),
      statusLabel: t(`targets.status.${display}`),
      line: targetLine(target.minValue, target.maxValue, loc),
      showsProgressBar,
      currentText: currentValue?.kind === 'ratio' ? formatMetricValueText(currentValue, loc) : undefined,
      currentMoney: currentValue?.kind === 'money' ? currentValue.value : undefined,
      goalMoney: target.minValue?.kind === 'money' ? target.minValue.value : undefined,
      goalDateText: target.goalDate === undefined ? undefined : formatDate(target.goalDate, loc),
      accountText: target.accountId === undefined ? undefined : props.accountLabel(target.accountId),
    }
  }),
)
</script>

<template>
  <div>
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-3 px-3 py-2">
      <li v-for="index in 3" :key="index" class="flex items-center gap-3">
        <Skeleton width="40%" height="1rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('targets.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('targets.errorTitle')"
      :body="t('targets.errorBody')"
      :retry-label="t('targets.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="targets.length === 0"
      :title="t('targets.emptyTitle')"
      :body="t('targets.emptyBody')"
    >
      <template #icon><NavIcon name="planning" /></template>
    </EmptyState>

    <ul v-else class="flex flex-col gap-[var(--space-cell)]">
      <li v-for="row in rows" :key="row.target.id">
        <Card class="flex min-w-0 items-start gap-[var(--space-cell)]">
          <span
            aria-hidden="true"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
          >
            <NavIcon name="planning" />
          </span>

          <div class="flex min-w-0 flex-1 flex-col gap-[var(--space-tight)]">
            <div class="flex min-w-0 items-center gap-[var(--space-cell)]">
              <p
                class="min-w-0 truncate text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]"
              >
                {{ row.target.name }}
              </p>
              <StatusTag :tone="row.tone" verdict size="sm">{{ row.statusLabel }}</StatusTag>
            </div>

            <p
              v-if="row.currentMoney !== undefined"
              class="flex min-w-0 flex-wrap items-baseline gap-x-1 text-[color:var(--color-text)]"
            >
              <MoneyAmount :value="row.currentMoney" size="hero" />
              <template v-if="row.goalMoney !== undefined">
                <span
                  class="text-[length:var(--text-step-3)] font-normal text-[color:var(--color-text-muted)]"
                  >/</span
                >
                <span class="text-[color:var(--color-text-muted)]"
                  ><MoneyAmount :value="row.goalMoney" size="figure" weight="regular"
                /></span>
              </template>
            </p>
            <p
              v-else-if="row.currentText !== undefined"
              class="truncate text-[length:var(--text-step-7)] font-semibold tabular-nums text-[color:var(--color-text)]"
            >
              {{ row.currentText }}
            </p>
            <p
              v-else
              class="text-[length:var(--text-step-7)] font-semibold text-[color:var(--color-text-muted)]"
            >
              —
            </p>

            <TargetProgressBar v-if="row.showsProgressBar" :target="row.target" />
            <TargetRangeMeter v-else-if="!hasProgressTreatment(row.target)" :target="row.target" />

            <p
              v-if="row.goalDateText !== undefined"
              class="truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ t('targets.targetDate') }}: {{ row.goalDateText }}
            </p>
            <p
              v-if="row.accountText !== undefined"
              class="truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ t('targets.accountOnLine') }}: {{ row.accountText }}
            </p>
            <p
              v-if="!row.showsProgressBar"
              class="truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ t('targets.targetLine') }}: {{ row.line }}
            </p>
          </div>

          <MenuPopover side="bottom" align="end">
            <template #trigger>
              <IconButton
                :disabled="deletingId === row.target.id"
                :aria-label="`${t('targets.actionsLabel')} ${row.target.name}`"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                  <circle cx="8" cy="3.5" r="1.4" />
                  <circle cx="8" cy="8" r="1.4" />
                  <circle cx="8" cy="12.5" r="1.4" />
                </svg>
              </IconButton>
            </template>
            <MenuItem @select="emit('delete', row.target.id)">
              {{ deletingId === row.target.id ? t('targets.deleting') : t('targets.deleteAction') }}
            </MenuItem>
          </MenuPopover>
        </Card>
      </li>
    </ul>
  </div>
</template>
